import { Paginated } from '@feathersjs/feathers/lib';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { computedFn } from 'mobx-utils';
import toast from 'react-hot-toast';
import { authStore, rootStore } from 'src/app';
import i18next from 'src/i18n';
import { intersect } from 'src/util/util';
import client from "../feathers";
import { DataType, ISpaceUser, IUser, Services, SpaceSettings, UserRole, UserStatus, UserType, FileType, ILog, IItem, NewsFeedSettings, IQuestion, IMeeting, IItemUser, SpaceType, Language } from '../types';
import { Data, AvatarImage } from './';

export class User extends Data<IUser> implements IUser {
  private static space_link_service = client.service(Services.SpaceUsers);
  avatar?:       string   = null;
  email:         string   = null;
  first_name?:   string   = null;
  last_name?:    string   = null;
  password?:     string   = null;
  phone?:        string   = null;
  language?:     Language = null;
  company_name?: string   = null;
  last_login?:   Date     = null;
  room_id?:      number   = null;
  description?:  string   = null;
  type?:         UserType = UserType.Default;
  project_id?:   number   = null;
  verified?:     boolean  = null;
  default_initials?: string = null;
  default_title?:    string = null;
  groupUsers:    number[] = []; // User_ids of members of this group. Empty if User is not a group
  userGroups:    number[] = []; // User_ids of groups of this user. Empty if user is a group.
  spaces?:       { [space_id: number]: SpaceSettings } = {};

  constructor(data: IUser) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.User, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      avatar:           observable,
      email:            observable,
      first_name:       observable,
      last_name:        observable,
      password:         observable,
      phone:            observable,
      room_id:          observable,
      groupUsers:       observable,
      userGroups:       observable,
      company_name:     observable,
      type:             observable,
      description:      observable,
      project_id:       observable,
      verified:         observable,
      spaces:           observable,
      default_initials: observable,
      default_title:    observable,
      
      getFullName:          computed,
      getInitials:          computed,
      getTitle:             computed,
      getAvatar:            computed,
      getWorkSpaceSettings: computed,
      getCurSpaceSettings:  computed,

      setSpaceSetting:    action,
      setCurSpaceSetting: action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here. 
    this.fromData(data);
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/

  public async save(patch?: Partial<IUser>): Promise<IUser> {
    try {
      if (this.id) {
        return await this.service.patch(this.id, patch ? this.toPatchData(patch) : this.toData()) as IUser;

      } else {
        const result: IUser = await this.service.create({ ...this.toData() }) as IUser;
        if (!result['id']) {
          console.error("Failed to create user:", result);
          throw ({ message: 'Failed to create user' });
        }
        runInAction(() => this.id = result['id']);
        return result;
      }
    } catch (error) {
      console.error("Failed to save object", this, error);
      throw (error)
    }
  }

  public static create(data?: IUser): User {
    return new User(data || {
      email:        "",
      groupUsers:   [],
      language:     Language.EN,
      type:         UserType.Default,
      userGroups:   [],
      workspace_id: rootStore.authStore.getCurWorkspaceID,
    });
  }

  protected fromData(data: IUser): void {
    super.fromData(data);
    this.verified         = data.verified;
    this.avatar           = data.avatar;
    this.default_initials = data.default_initials;
    this.default_title    = data.default_title;
    this.email            = data.email;
    this.first_name       = data.first_name;
    this.last_name        = data.last_name;
    this.password         = data.password;
    this.phone            = data.phone;
    this.company_name     = data.company_name;
    this.last_login       = data.last_login;
    this.language         = data.language;
    this.description      = data.description;
    this.type             = data.type;
    this.room_id          = data.room_id && Number(data.room_id);
    this.project_id       = data.project_id && Number(data.project_id);
    this.groupUsers   = (data.groupUsers && data.groupUsers.map(u => Number(u))) || [];
    this.userGroups   = (data.userGroups && data.userGroups.map(g => Number(g))) || [];
    this.spaces       = data.spaces ? (Array.isArray(data.spaces) ? data.spaces.reduce((res, val) => {
      res[Number(val.space_id)] = val;
      return res;
    }, {}) : data.spaces) : {};
  }

  public toData(): any {
    return {
      ...super.toData(),
      avatar:       this.avatar || null,
      email:        this.email  || null,
      phone:        this.phone  || null,
      type:         this.type   || null,
      first_name:   this.first_name || null,
      last_name:    this.last_name  || null,
      password:     this.password   || undefined,
      language:     this.language   || Language.EN,
      company_name: this.company_name || null,
      last_login:   this.last_login   || null,
      description:  this.description  || null,
      created_by:   null,
      updated_by:   null,
      default_initials: this.default_initials || null,
      default_title:    this.default_title || null,
    };
  }

  public toPatchData(patch: Partial<IUser>): any {
    return {
      ...super.toPatchData(patch),
      avatar:       "avatar" in patch ? patch.avatar:             undefined,
      email:        "email" in patch ? patch.email:               undefined,
      phone:        "phone" in patch ? patch.phone:               undefined,
      first_name:   "first_name" in patch ? patch.first_name:     undefined,
      last_name:    "last_name" in patch ? patch.last_name:       undefined,
      last_login:   "last_login" in patch ? patch.last_login:     undefined,
      company_name: "company_name" in patch ? patch.company_name: undefined,
      description:  "description" in patch ? patch.description:   undefined,
      type:         "type" in patch ? patch.type:                 undefined,
      language:     "language" in patch ? patch.language:         undefined,
      default_title:    "default_title" in patch ? patch.default_title:       undefined,
      default_initials: "default_initials" in patch ? patch.default_initials: undefined,
    };
  }

  saveSpaceSettings = async (): Promise<void> => {
    try {
      if (this.workspace_id && this.getWorkSpaceSettings.id) {
        await User.space_link_service.patch(this.getWorkSpaceSettings.id, toJS(this.getWorkSpaceSettings));
        return;
      }
    } catch (error) {
      // rootStore.displaySystemMessage(1000, {text: "Failed to save object", type: "error"});
      console.error("Failed to save object", this, error);
      return;
    }
  }


  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/


  get canEdit(): boolean {
    if(!rootStore.spaceStore.getCurSpace) return false;
    const space_id = rootStore.spaceStore.getCurSpace?.id;
    return [UserRole.Leader, UserRole.Owner].includes(this.getRole(space_id));
  }

  public canEditSpace = computedFn( (space_id): boolean => {
    return [UserRole.Leader, UserRole.Owner].includes(this.getRole(space_id));
  });

  public get canView(): boolean {
    const space_id = rootStore.spaceStore.getCurSpace?.id;
    return Boolean(this.inSpace(space_id));
  }

  public get displayName(): string {
    const display_as = this.spaces[rootStore.authStore.getCurSpaceID]?.display_as;
    if("initials" === display_as || display_as === "title")  {
      return this.spaces[rootStore.authStore.getCurSpaceID][display_as] || (this.getFullName.length ? this.getFullName : this.email);
    }
    return this.getFullName.length ? this.getFullName : this.email;
  }

  public get getFullName(): string {
    return [this.first_name, this.last_name].filter(Boolean).join(" ");
  }

  public get getInitials(): string {
    return (rootStore.authStore.getCurSpaceID && this.spaces[rootStore.authStore.getCurSpaceID]?.initials
        ? this.spaces[rootStore.authStore.getCurSpaceID].space_type == SpaceType.Workspace ? this.default_initials : this.spaces[rootStore.authStore.getCurSpaceID].initials
        : this.getFullName.split(" ").map(s => s[0]).join("")
      ).toUpperCase();
  }

  public getInitialsBySpace = computedFn((space_id: number) => {
    return this.spaces[space_id]?.initials?.toUpperCase() || "";
  });

  public getTitleBySpace = computedFn((space_id: number) => {
    return this.spaces[space_id]?.title || "";
  });

  public get getTitle(): string {
    if(this.type === UserType.Group) return this.getFullName;
    return rootStore.spaceStore.getCurSpace?.id 
    ? this.spaces[rootStore.spaceStore.getCurSpace?.id]?.title
    : "";
  }

  public get getAvatar(): string {
    //if(!this.avatar) return 'https://gravatar.com/avatar/da8c18f8ec544e3d6e4c98fc4dbddf71?s=400&d=mp&r=x';
    if (!this.avatar) return null;
    return `${process.env.STORAGE_URL}/${this.avatar}`;
  }

  public get isVerified(): boolean {
    return this.verified
  }

  public get lastLogin(): Date | null {
    const l = new Date(this.last_login).getTime();
    const c = new Date(this.created_at).getTime();
    return l === c ? null : this.last_login;
  }

  public get getWorkSpaceSettings(): SpaceSettings {
    return this.inSpace(this.workspace_id) ? this.spaces[this.workspace_id] : {};
  };
  public getStatus = computedFn((space_id: number): UserStatus => {
    return this.inSpace(space_id) ? this.spaces[space_id].status : undefined;
  });

  public getRole = computedFn((space_id: number): UserRole => {
    return this.inSpace(space_id) ? this.spaces[space_id].role : undefined;
  });

  public isOnline = computedFn((space_id: number): boolean => {
    return this.inSpace(space_id) ? !!this.spaces[space_id].online : false;
  });
  public get getSpaces(): number[] {
    return Object.keys(this.spaces).map(id => Number(id));
  }

  public inSpace = computedFn((space_id: number): boolean => {
    return !!this.spaces[space_id];
  });

  public get getGroupUsers(): number[] {
    return this.groupUsers;
  }

  public getGroupUsersBySpace = computedFn((space_id: number): number[] => {
    return this.groupUsers.filter(g => !!rootStore.userStore.container[g]?.spaces[space_id]);
  });

  /**
   * @return array with user_ids of all groups the user belongs to. 
   *         This includes the id of the user itself as well.
   */
  public get getUserGroups(): number[] {
    return [this.id, ...this.userGroups];
  }


  /**
   * @param data 
   * @param user 
   * 
   * @return <true> if @user is affected by the log entry @data according to its user settings and the affected item. 
   */
  public isAffected = async (data: ILog, item: IItem): Promise<boolean> => {
    if (!data || !item) {
      console.error("Insufficient parameters", data, item);
      return false;
    }
    switch (this.spaces[data.room_id].news_feed) {
      case NewsFeedSettings.All:
        return true;
      case NewsFeedSettings.Affected:
        if ([(item as IQuestion)["from"], (item as IQuestion)["to"], (item as IMeeting)["leader_id"]].includes(this.id))
          return true;

        const links: Paginated<IItemUser> = await client.service(Services.ItemUsers).find({
          query: {
            $limit: 0,
            item_id: data.item_id,
            user_id: this.id,
            link_type: { $in: ["participant", "watcher"] }
          }
        });

        return Boolean(links.total);
      case NewsFeedSettings.None:
      default:
        return false;
    }
  }

  public getSpaceSetting = computedFn(<K extends keyof SpaceSettings>(space_id: number, prop: K): any => {
    if (!this.spaces[space_id]) return null;
    return this.spaces[space_id][prop];
  });

  public get getCurSpaceSettings(): SpaceSettings {
    if(!this.spaces[authStore.getCurSpaceID]) return null;
    return this.spaces[authStore.getCurSpaceID];
  }

  /**
   * @return id of first group of given space or the user_id if no such group exists.
   */
  public getRepresentative = computedFn((space_id): number => {
    if(this.userGroups.length) {
      const roomGroups = rootStore.userStore.getUsersBySpace(rootStore.authStore.getCurRoomID, [UserType.Group]);
      const groups = intersect(roomGroups.map(g => g.id), this.userGroups);
      return groups.length ? groups[0] : rootStore.authStore.getCurUser.id;
    } else {
      return this.id;
    }
  });

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/
  public changeLanguage = async (language: Language) => {
    i18next.changeLanguage(language, async () => await this.set("language", language, true));
  }

  public setRole = async (space_id: number, role: UserRole): Promise<void> => {
    const data = await User.space_link_service.find({
      query: {
        $limit: 1,
        $select: ["id"],
        user_id: this.id,
        space_id: space_id,
      }
    });
    if (!data || !data.data || !data.data.length) {
      console.error("Failed to fetch space member");
      return;
    }
    const id = Number(data.data[0].id);

    await User.space_link_service.patch(id, { role: role });

    runInAction(() => this.spaces[space_id].role = role);
  }

  public setAvatar = async (uri: string | ArrayBuffer, size: number, mime: string): Promise<void> => {
    try {

      const avatar = await AvatarImage.create({
        uri:          uri,
        title:        `user-${this.id}-avatar`,
        mime:         mime,
        size:         size,
        workspace_id: this.workspace_id,
        type:         FileType.Avatar,
      });

      await avatar.upload();

      if (avatar.key) {
        await this.set('avatar', avatar.key, true);
      }

    } catch (error) {
      console.error('Failed to save user avatar', error)
    }
  }

  public setSpaceSetting = async <K extends keyof SpaceSettings>(space_id: number, prop: K, value: SpaceSettings[K], save?: boolean) => {
    if (!this.spaces[space_id]) {
      console.error("User is not a member of this space", this, space_id);
      return;
    }
    this.spaces[space_id][prop] = value;
    if (save) await User.space_link_service.patch(this.spaces[space_id].id, toJS(this.spaces[space_id]));
  }

  public setCurSpaceSetting = async <K extends keyof SpaceSettings>(prop: K, value: SpaceSettings[K], save?: boolean) => {
    if (!this.spaces[authStore.getCurSpaceID]) {
      console.error("User is not a member of this space", this, authStore.getCurSpaceID);
      return;
    }
    this.spaces[authStore.getCurSpaceID][prop] = value;
    if (save) await User.space_link_service.patch(this.spaces[authStore.getCurSpaceID].id, toJS(this.spaces[authStore.getCurSpaceID]));
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/


  public linkSpace = async (space_id: number, space_type: SpaceType, role: UserRole, status: UserStatus): Promise<void> => {
    try {
      const space_user: ISpaceUser = {
        user_id:      this.id,
        workspace_id: rootStore.authStore.getCurWorkspaceID,
        initials:     this.default_initials,
        title:        this.default_title,
        space_id,
        role,
        space_type,
        status
      }
      return User.space_link_service.create(space_user);
    } catch (err) {
      console.error("Failed to link user to space", err);
      return;
    }
  }

  public unlinkSpace = async (space_id: number): Promise<void> => {
    if (!space_id) {
      console.error("Invalid space_id", space_id);
      throw new Error("Invalid space id");
    }

    try {
      const space_users: Paginated<ISpaceUser> = await client.service(Services.SpaceUsers).find({
        query: {
          space_id: space_id,
          user_id: this.id
        }
      });
      for (const ts of space_users.data) { // In case accidentally more than one link was created
        await User.space_link_service.remove(ts.id);
      }
    } catch (err) {
      console.error("Failed to unlink user from space", err);
      throw err;
    }
  }

  public addGroupUser = async (user_id: number): Promise<void> => {
    try {
      if (this.groupUsers.includes(user_id)) {
        toast.error(i18next.t("user_already_in_group"));
        return;
      }
      await client.service(Services.GroupUsers).create({
        user_id: user_id,
        group_id: this.id,
        workspace_id: this.workspace_id,
      });
      toast.success(i18next.t("user_added_to_group"));
    } catch (err) {
      console.error("Failed to create group user", err);
      toast.error(i18next.t("user_already_in_group_failed"));
    }
  }

  public removeGroupUser = async (user_id: number): Promise<void> => {
    try {
      if (!this.groupUsers.includes(user_id)) return;

      const linkData = await client.service(Services.GroupUsers).find({
        query: {
          user_id: user_id,
          group_id: this.id,
        }
      });
      if (!linkData || !linkData.data || !linkData.data.length) {
        console.error("Failed to fetch space member");
        return;
      }
      await client.service(Services.GroupUsers).remove(Number(linkData.data[0].id));
    } catch (err) {
      console.error("Failed to remove group user", err);
    }
  }
}
//#endregion